import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface AuthConfig {
  authMode?: string;
  requireMfa?: boolean | null;
  enablePasskeys?: boolean | null;
  allowLocalLogin?: boolean;
}

interface SsoProvider {
  id: number;
  providerType: string;
  displayName: string | null;
  domain: string | null;
  isEnabled: boolean;
  metadataUrl?: string | null;
  clientId?: string | null;
  hasClientSecret?: boolean;
  isRegisteredInEntra?: boolean;
}

interface SecurityData {
  authConfig: AuthConfig | null;
  ssoProviders: SsoProvider[];
  ssoEnabled: boolean;
}

interface SsoFormState {
  providerType: string;
  displayName: string;
  metadataUrl: string;
  clientId: string;
  clientSecret: string;
  domain: string;
}

const DEFAULT_SSO_FORM: SsoFormState = {
  providerType: "OIDC",
  displayName: "",
  metadataUrl: "",
  clientId: "",
  clientSecret: "",
  domain: ""
};

const PROVIDER_TYPES = [
  { value: "Okta", label: "Okta" },
  { value: "OneLogin", label: "OneLogin" },
  { value: "EntraID", label: "Microsoft Entra ID (Azure AD)" },
  { value: "PingID", label: "PingID" },
  { value: "PingFederate", label: "PingFederate" },
  { value: "SAML", label: "SAML 2.0 (Generic)" },
  { value: "OIDC", label: "OIDC (Generic OpenID Connect)" }
];

const OIDC_PROVIDERS = ["Okta", "OneLogin", "EntraID", "PingID", "PingFederate", "OIDC"];

export default function SecurityPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();
  const [data, setData] = useState<SecurityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailedProviders, setDetailedProviders] = useState<SsoProvider[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SsoFormState>(DEFAULT_SSO_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; issuer?: string; error?: string } | null>(null);
  const [authMode, setAuthModeState] = useState("V10");
  const [savingAuthMode, setSavingAuthMode] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/security");
      if (resp.ok) {
        const d = await resp.json();
        setData(d);
        setAuthModeState(d?.authConfig?.authMode ?? "V10");
      }
    } catch {
      // Graceful
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const fetchDetailedSso = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/security/sso");
      if (resp.ok) {
        const d = await resp.json();
        setDetailedProviders(d.configs ?? []);
      }
    } catch {
      // Fail silently -- not all users are admins
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchData();
    fetchDetailedSso();
  }, [fetchData, fetchDetailedSso]);

  const hasSso = subscription?.enableEnterpriseSso ?? false;
  const hasMfa = subscription?.enableAdvancedMfa ?? false;
  const hasPasskeys = subscription?.enablePasskeys ?? false;
  const ssoProviders = Array.isArray(data?.ssoProviders) ? data!.ssoProviders : [];
  const currentAuthMode = data?.authConfig?.authMode ?? "V10";
  const ssoEffectivelyActive = hasSso || currentAuthMode === "EntraId" || currentAuthMode === "Dual";
  const isOidcProvider = OIDC_PROVIDERS.includes(form.providerType);

  async function handleTestConnection() {
    if (!form.metadataUrl) return;
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await api.messaging.post("admin/security/sso/test", {
        providerType: form.providerType,
        metadataUrl: form.metadataUrl
      });
      const result = await resp.json();
      setTestResult(result);
    } catch {
      setTestResult({ success: false, error: "Connection test failed." });
    } finally {
      setTesting(false);
    }
  }

  async function handleSaveProvider() {
    if (!form.metadataUrl) {
      toastAlert.displayToast(Severity.Error, "Metadata URL is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        providerType: form.providerType,
        displayName: form.displayName || undefined,
        metadataUrl: form.metadataUrl,
        clientId: form.clientId || undefined,
        clientSecret: form.clientSecret || undefined,
        domain: form.domain || undefined
      };
      const resp = editingId !== null
        ? await api.messaging.put(`admin/security/sso/${editingId}`, body)
        : await api.messaging.post("admin/security/sso", body);

      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, editingId !== null ? "Provider updated." : "Provider added and registered.");
        setShowAddForm(false);
        setEditingId(null);
        setForm(DEFAULT_SSO_FORM);
        setTestResult(null);
        await fetchData();
        await fetchDetailedSso();
      } else {
        const err = await resp.json().catch(() => ({}));
        toastAlert.displayToast(Severity.Error, err?.error ?? "Failed to save provider.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save provider.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProvider(id: number, displayName: string | null) {
    if (!confirm(`Remove SSO provider "${displayName ?? id}"? This cannot be undone.`)) return;
    try {
      const resp = await api.messaging.delete(`admin/security/sso/${id}`);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Provider removed.");
        await fetchData();
        await fetchDetailedSso();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to remove provider.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove provider.");
    }
  }

  function handleEditProvider(p: SsoProvider) {
    setEditingId(p.id);
    setForm({
      providerType: p.providerType,
      displayName: p.displayName ?? "",
      metadataUrl: p.metadataUrl ?? "",
      clientId: p.clientId ?? "",
      clientSecret: "",
      domain: p.domain ?? ""
    });
    setShowAddForm(true);
    setTestResult(null);
  }

  async function handleSetAuthMode(mode: string) {
    setSavingAuthMode(true);
    try {
      const resp = await api.messaging.put("admin/security/auth-mode", { authMode: mode });
      if (resp.ok) {
        setAuthModeState(mode);
        setData(prev => prev ? { ...prev, authConfig: { ...(prev.authConfig ?? {}), authMode: mode } } : prev);
        toastAlert.displayToast(Severity.Success, "Login mode updated.");
      } else {
        const err = await resp.json().catch(() => ({}));
        toastAlert.displayToast(Severity.Error, err?.error ?? "Failed to update login mode.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to update login mode.");
    } finally {
      setSavingAuthMode(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-slate-gray-500 text-sm">Loading security settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-gray-800">Security & Authentication</h2>
        <p className="text-sm text-slate-gray-500 mt-1">
          Configure how your team logs in, set up identity providers, and manage break-glass access.
        </p>
      </div>

      {/* Current Auth Status */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-3">Authentication Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <StatusCard
            label="Login Method"
            value={
              currentAuthMode === "EntraId"
                ? "SSO Required"
                : currentAuthMode === "Dual"
                  ? "SSO + Password"
                  : "Password Only"
            }
            active={currentAuthMode !== "V10"}
          />
          <StatusCard
            label="Multi-Factor Authentication"
            value={data?.authConfig?.requireMfa ? "Required" : hasMfa ? "Available" : "Not included"}
            active={!!data?.authConfig?.requireMfa}
          />
        </div>
      </div>

      {/* SSO Section */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Single Sign-On (SSO)</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Allow your team to log in with your company's identity provider -- Microsoft Entra ID, Okta, PingID, OneLogin, or any OIDC/SAML provider.
        </p>

        {ssoEffectivelyActive ? (
          <>
            {/* Auth Mode Toggle -- shown when at least one provider is configured */}
            {(detailedProviders.length > 0 || ssoProviders.length > 0) && (
              <div className="mb-5 p-4 bg-slate-gray-50 rounded-lg border border-slate-gray-200">
                <div className="text-sm font-medium text-slate-gray-700 mb-2">Login Mode</div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { mode: "V10", label: "Password only", desc: "SSO available but not required" },
                    { mode: "Dual", label: "SSO + Password", desc: "Users choose either method" },
                    { mode: "EntraId", label: "SSO required", desc: "Password login blocked (except break-glass users)" }
                  ].map(({ mode, label, desc }) => (
                    <button
                      key={mode}
                      disabled={savingAuthMode}
                      onClick={() => handleSetAuthMode(mode)}
                      className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg border text-left transition ${
                        authMode === mode
                          ? "border-primary bg-primary-bg text-cornflower-blue-800"
                          : "border-slate-gray-200 bg-white text-slate-gray-700 hover:border-cornflower-blue-300"
                      } ${savingAuthMode ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs text-slate-gray-500 mt-0.5">{desc}</div>
                    </button>
                  ))}
                </div>
                {authMode === "EntraId" && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Users with break-glass access can still log in with email + password. Manage this per-user in the Users section.
                  </div>
                )}
              </div>
            )}

            {/* Provider list */}
            {detailedProviders.length > 0 ? (
              <div className="space-y-2 mb-4">
                {detailedProviders.map((p) => (
                  <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${p.isEnabled ? "border-emerald-200 bg-emerald-50/50" : "border-slate-gray-200 bg-slate-gray-50"}`}>
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${p.isEnabled ? "bg-emerald-500" : "bg-slate-gray-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-gray-800">{p.displayName ?? p.providerType}</div>
                      <div className="text-xs text-slate-gray-500">
                        {p.providerType}{p.domain ? ` -- ${p.domain}` : ""}{!p.isRegisteredInEntra ? " -- pending activation" : ""}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${p.isEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-gray-100 text-slate-gray-500"}`}>
                      {p.isEnabled ? "Active" : "Pending"}
                    </span>
                    <button
                      onClick={() => handleEditProvider(p)}
                      className="text-xs text-primary hover:text-cornflower-blue-800 flex-shrink-0"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProvider(p.id, p.displayName)}
                      className="text-xs text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : ssoProviders.length > 0 ? (
              <div className="space-y-2 mb-4">
                {ssoProviders.map((p) => (
                  <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${p.isEnabled ? "border-emerald-200 bg-emerald-50/50" : "border-slate-gray-200 bg-slate-gray-50"}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${p.isEnabled ? "bg-emerald-500" : "bg-slate-gray-400"}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-gray-800">{p.displayName ?? p.providerType}</div>
                      <div className="text-xs text-slate-gray-500">{p.providerType}{p.domain ? ` -- ${p.domain}` : ""}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.isEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-gray-100 text-slate-gray-500"}`}>
                      {p.isEnabled ? "Active" : "Inactive"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-gray-50 border border-slate-gray-200 rounded-lg p-4 mb-4 text-sm text-slate-gray-600">
                No identity providers configured yet. Add one below to get started.
              </div>
            )}

            {/* Add / Edit form */}
            {hasSso && (
              <>
                {!showAddForm ? (
                  <button
                    onClick={() => { setShowAddForm(true); setEditingId(null); setForm(DEFAULT_SSO_FORM); setTestResult(null); }}
                    className="text-sm font-medium text-primary hover:text-cornflower-blue-800 flex items-center gap-1"
                  >
                    <span className="text-lg leading-none">+</span> Add identity provider
                  </button>
                ) : (
                  <div className="border border-slate-gray-200 rounded-lg p-4 space-y-3">
                    <div className="text-sm font-semibold text-slate-gray-800">{editingId !== null ? "Edit provider" : "Add identity provider"}</div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-gray-600 mb-1 block">Provider type</label>
                        <select
                          value={form.providerType}
                          onChange={e => setForm({ ...form, providerType: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-slate-gray-200 rounded-lg bg-white"
                        >
                          {PROVIDER_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-gray-600 mb-1 block">Display name</label>
                        <input
                          type="text"
                          placeholder={form.providerType + " SSO"}
                          value={form.displayName}
                          onChange={e => setForm({ ...form, displayName: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-slate-gray-200 rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-slate-gray-600 mb-1 block">
                        {form.providerType === "SAML" ? "SAML metadata URL" : "OIDC discovery URL"}
                        <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          placeholder={form.providerType === "SAML"
                            ? "https://idp.example.com/metadata.xml"
                            : "https://idp.example.com/.well-known/openid-configuration"}
                          value={form.metadataUrl}
                          onChange={e => { setForm({ ...form, metadataUrl: e.target.value }); setTestResult(null); }}
                          className="flex-1 px-3 py-2 text-sm border border-slate-gray-200 rounded-lg"
                        />
                        <button
                          onClick={handleTestConnection}
                          disabled={testing || !form.metadataUrl}
                          className="px-3 py-2 text-xs font-medium text-primary border border-cornflower-blue-200 rounded-lg hover:bg-primary-bg disabled:opacity-50"
                        >
                          {testing ? "Testing..." : "Test"}
                        </button>
                      </div>
                      {testResult && (
                        <div className={`mt-1 text-xs px-2 py-1 rounded ${testResult.success ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}>
                          {testResult.success ? `Connected -- Issuer: ${testResult.issuer ?? "detected"}` : testResult.error}
                        </div>
                      )}
                    </div>

                    {isOidcProvider && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-gray-600 mb-1 block">Client ID</label>
                          <input
                            type="text"
                            value={form.clientId}
                            onChange={e => setForm({ ...form, clientId: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-slate-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-gray-600 mb-1 block">
                            Client secret{editingId !== null ? " (leave blank to keep current)" : ""}
                          </label>
                          <input
                            type="password"
                            value={form.clientSecret}
                            onChange={e => setForm({ ...form, clientSecret: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-slate-gray-200 rounded-lg"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-medium text-slate-gray-600 mb-1 block">Email domain (for auto-routing)</label>
                      <input
                        type="text"
                        placeholder="@yourcompany.com"
                        value={form.domain}
                        onChange={e => setForm({ ...form, domain: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-gray-200 rounded-lg"
                      />
                      <div className="text-xs text-slate-gray-400 mt-0.5">Users with this email domain are automatically directed to this provider.</div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-700">
                      <strong>Redirect URI for all providers:</strong>{" "}
                      <code className="bg-white px-1 py-0.5 rounded select-all">https://login.velaro.com/oauth2/callback</code>
                      <p className="mt-0.5">Your IT team must add this as an allowed redirect URI in the identity provider settings.</p>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setShowAddForm(false); setEditingId(null); setForm(DEFAULT_SSO_FORM); setTestResult(null); }}
                        className="px-4 py-2 text-sm text-slate-gray-600 border border-slate-gray-200 rounded-lg hover:bg-slate-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProvider}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary disabled:opacity-50"
                      >
                        {saving ? "Saving..." : editingId !== null ? "Update provider" : "Add provider"}
                      </button>
                    </div>
                  </div>
                )}

                <SsoSetupGuide />
              </>
            )}
          </>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">SSO is not included in your current plan</p>
            <p className="mt-1">Upgrade to an Enterprise plan to enable Single Sign-On for your team. Contact sales@velaro.com for pricing.</p>
          </div>
        )}
      </div>

      {/* MFA Section */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Multi-Factor Authentication (MFA)</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Add an extra layer of security by requiring a second form of verification when logging in.
        </p>

        {hasMfa ? (
          <div className="space-y-3">
            <div className={`${data?.authConfig?.requireMfa ? "bg-emerald-50 border-emerald-200" : "bg-primary-bg border-cornflower-blue-200"} border rounded-lg p-4 text-sm`}>
              <p className={`font-medium ${data?.authConfig?.requireMfa ? "text-emerald-800" : "text-cornflower-blue-800"}`}>
                {data?.authConfig?.requireMfa
                  ? "MFA is required for all users on this site"
                  : "MFA is available but not required"}
              </p>
              <p className={`mt-1 ${data?.authConfig?.requireMfa ? "text-emerald-600" : "text-primary"}`}>
                {data?.authConfig?.requireMfa
                  ? "All users must set up a verification method. Users who haven't configured MFA will be prompted at login."
                  : "MFA can be enforced by your Velaro administrator. Users can optionally set it up for extra security."}
              </p>
            </div>
            <div className="text-sm text-slate-gray-600">
              <p className="font-medium mb-2">Supported MFA methods:</p>
              <ul className="space-y-1.5 ml-4">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />SMS verification code</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />Email one-time passcode (OTP)</li>
              </ul>
            </div>
            <p className="text-xs text-slate-gray-500">When SSO is active, MFA and passkey policies are controlled by your identity provider.</p>
          </div>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">MFA is not included in your current plan</p>
            <p className="mt-1">Upgrade to enable multi-factor authentication. Contact sales@velaro.com for pricing.</p>
          </div>
        )}
      </div>

      {/* Passkeys */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Passkeys / FIDO2</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Biometric or hardware key authentication. When SSO is active, passkey policy is controlled by your identity provider.
        </p>
        {hasPasskeys ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
            <p className="font-medium text-emerald-800">Passkeys are available for your account</p>
            <p className="text-emerald-600 mt-1">Once enabled by your administrator, you can register a passkey from your profile settings.</p>
          </div>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">Passkeys are not included in your current plan</p>
            <p className="mt-1">Contact sales@velaro.com to upgrade.</p>
          </div>
        )}
      </div>

      <SupportRequestForm />
    </div>
  );
}

function SsoSetupGuide() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-gray-200 rounded-lg mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-gray-50 transition"
        aria-expanded={expanded}
      >
        <div>
          <span className="text-sm font-medium text-slate-gray-800">Setup guide -- what your IT team needs</span>
          <span className="block text-xs text-slate-gray-500 mt-0.5">Step-by-step for each identity provider</span>
        </div>
        <svg className={`w-5 h-5 text-slate-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-slate-gray-200 p-4 space-y-4">
          <ProviderGuide name="Microsoft Entra ID (Azure AD)" recommended items={[
            "Provide your Azure AD Tenant ID (Azure Portal > Microsoft Entra ID > Overview)",
            "Tell us which email domain(s) to route (e.g., @yourcompany.com)",
            "Microsoft's OpenID Connect handles everything else automatically"
          ]} />
          <ProviderGuide name="Okta" items={[
            "Create an OIDC Web Application in Okta Admin > Applications > Create App Integration",
            "Set redirect URI to: https://login.velaro.com/oauth2/callback",
            "Send us: Client ID, Client Secret, and your Okta domain (e.g., yourcompany.okta.com)",
            "Tell us the email domain to route"
          ]} />
          <ProviderGuide name="PingID / PingFederate" items={[
            "Create an OIDC client in PingFederate Admin > OAuth Clients",
            "Set redirect URI to: https://login.velaro.com/oauth2/callback",
            "Send us: Client ID, Client Secret, and the OIDC discovery URL (.well-known/openid-configuration)",
            "Tell us the email domain to route"
          ]} />
          <ProviderGuide name="OneLogin" items={[
            "Create an OIDC connector: Apps > Add App > search 'OpenID Connect'",
            "Set redirect URI to: https://login.velaro.com/oauth2/callback",
            "Send us: Client ID, Client Secret, and your OneLogin subdomain",
            "Tell us the email domain to route"
          ]} />
          <ProviderGuide name="Other SAML / OIDC provider" items={[
            "Provide the OIDC metadata URL (ends in /.well-known/openid-configuration) or SAML metadata XML URL",
            "If OIDC: create an app and send us Client ID + Client Secret",
            "Set redirect URI to: https://login.velaro.com/oauth2/callback"
          ]} />
          <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 text-xs text-primary-active">
            <strong>Break-glass access:</strong> Even with SSO-required mode, designated users can be given password login access from the Users section. This ensures you're never locked out if your identity provider has an outage.
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderGuide({ name, items, recommended }: { name: string; items: string[]; recommended?: boolean }) {
  return (
    <div className="bg-slate-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-slate-gray-800">{name}</span>
        {recommended && <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-bold">RECOMMENDED</span>}
      </div>
      <ul className="text-sm text-slate-gray-600 space-y-1 ml-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-primary font-bold mt-0.5">{i + 1}.</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusCard({ label, value, active }: { label: string; value: string; active: boolean }) {
  return (
    <div className={`rounded-lg p-3 border ${active ? "border-emerald-200 bg-emerald-50" : "border-slate-gray-200 bg-slate-gray-50"}`}>
      <div className="text-xs text-slate-gray-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${active ? "text-emerald-700" : "text-slate-gray-600"}`}>{value}</div>
    </div>
  );
}

function SupportRequestForm() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [subject, setSubject] = useState("SSO Setup Request");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      const resp = await api.messaging.post("admin/support-request", { subject, message });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Support request sent successfully.");
        setMessage("");
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to send support request. Please try again.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to send support request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-5">
      <h3 className="font-semibold text-cornflower-blue-800 mb-2">Need help with security?</h3>
      <p className="text-sm text-primary-active mb-4">
        Our team can help you set up SSO, configure MFA policies, or answer questions about securing your Velaro account.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="support-subject" className="block text-xs font-medium text-primary-active mb-1">Subject</label>
          <input id="support-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-cornflower-blue-200 rounded-lg bg-white text-slate-gray-800 focus-visible:outline-2 focus-visible:outline-primary" />
        </div>
        <div>
          <label htmlFor="support-message" className="block text-xs font-medium text-primary-active mb-1">Message</label>
          <textarea id="support-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe what you need help with..."
            className="w-full px-3 py-2 text-sm border border-cornflower-blue-200 rounded-lg bg-white text-slate-gray-800 resize-y focus-visible:outline-2 focus-visible:outline-primary" />
        </div>
        <button type="submit" disabled={sending || !message.trim()}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary transition disabled:opacity-50 disabled:cursor-not-allowed">
          {sending ? "Sending..." : "Send to Support"}
        </button>
      </form>
    </div>
  );
}
